import type { CompanyIdType } from "@biteasy/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";

import { authClient } from "@/auth";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import {
	Combobox,
	ComboboxLabel,
	ComboboxOption,
} from "@/components/catalyst/Combobox";
import {
	ErrorMessage,
	Field,
	FieldGroup,
	Label,
} from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { TextLink } from "@/components/catalyst/Text";
import { setBetterAuthError } from "@/hooks/use-better-auth-error";
import { useTurnstile } from "@/hooks/use-turnstile";
import type { Country } from "@/util/countries";
import { COUNTRIES } from "@/util/countries";
import type { Schema, SchemaInput } from "./schema";
import { schema } from "./schema";

const COMPANY_ID_TYPES: { value: CompanyIdType; label: string }[] = [
	{ value: "bn", label: "Business Number (Canada)" },
	{ value: "gst", label: "GST Number" },
	{ value: "ein", label: "EIN (US)" },
	{ value: "vat", label: "VAT Number (EU/UK)" },
	{ value: "abn", label: "ABN (Australia)" },
	{ value: "other", label: "Other" },
];

export function Form() {
	const router = useRouter();
	const { getCaptchaHeaders, reset: resetTurnstile } = useTurnstile();

	// Read plan and billingPeriod from query params
	const queryParams = useSearch({ from: "/auth/signup" });

	const {
		register,
		handleSubmit,
		setError,
		control,
		formState: { errors, isSubmitting },
	} = useForm<SchemaInput>({
		resolver: zodResolver(schema),
		defaultValues: async () => {
			if (import.meta.env.DEV) {
				const { faker } = await import("@faker-js/faker");
				return {
					name: faker.person.fullName(),
					email: faker.internet.email(),
					password: "password123",
					country: "CA",
					companyName: undefined,
					companyIdType: undefined,
					companyIdValue: undefined,
				};
			}
			return {
				name: "",
				email: "",
				password: "",
				country: "",
				companyName: undefined,
				companyIdType: undefined,
				companyIdValue: undefined,
			};
		},
	});

	const setServerError = setBetterAuthError(setError);

	const onSubmit = handleSubmit(async (data) => {
		const typedData = data as Schema;
		await authClient.signUp.email({
			...typedData,
			callbackURL: `${window.location.origin}/dashboard?success=verify-email`,
			fetchOptions: {
				headers: getCaptchaHeaders(),
				onSuccess: async () => {
					// Navigate to embedded checkout for subscription setup
					const selectedPlan = queryParams.plan ?? "standard";
					const selectedBillingPeriod = queryParams.billingPeriod ?? "monthly";

					router.navigate({
						to: "/checkout",
						search: {
							plan: selectedPlan,
							billingPeriod: selectedBillingPeriod,
						},
					});
				},
				onError: (ctx) => {
					resetTurnstile();
					setServerError(ctx.error.code);
				},
			},
		});
	}, console.log);

	return (
		<form className="space-y-6" onSubmit={onSubmit}>
			{errors.root?.message && (
				<AlertBar title="Woops!" variant="error">
					{errors.root?.message}
				</AlertBar>
			)}
			<FieldGroup>
				<Field>
					<Label>Full Name</Label>
					<Input type="text" {...register("name")} placeholder="Full name" />
					{errors.name?.message && (
						<ErrorMessage>{errors.name.message}</ErrorMessage>
					)}
				</Field>
				<Field>
					<Label>Email address</Label>
					<Input
						type="email"
						{...register("email")}
						placeholder="Email address"
					/>
					{errors.email?.message && (
						<ErrorMessage>{errors.email.message}</ErrorMessage>
					)}
				</Field>
				<Field>
					<Label>Password</Label>
					<Input
						type="password"
						{...register("password")}
						placeholder="Password"
					/>
					{errors.password?.message && (
						<ErrorMessage>{errors.password.message}</ErrorMessage>
					)}
				</Field>
				<Field>
					<Label>Country</Label>
					<Controller
						name="country"
						control={control}
						render={({ field }) => (
							<Combobox
								options={COUNTRIES}
								value={COUNTRIES.find((c) => c.code === field.value)}
								onChange={(country: Country | null) =>
									field.onChange(country?.code ?? "")
								}
								displayValue={(country) => country?.name}
								placeholder="Select a country"
								aria-label="Country"
							>
								{(country) => (
									<ComboboxOption value={country}>
										<ComboboxLabel>{country.name}</ComboboxLabel>
									</ComboboxOption>
								)}
							</Combobox>
						)}
					/>
					{errors.country?.message && (
						<ErrorMessage>{errors.country.message}</ErrorMessage>
					)}
				</Field>
				<Field>
					<Label>Company Name (Optional)</Label>
					<Input
						type="text"
						{...register("companyName")}
						placeholder="Company name"
					/>
					{errors.companyName?.message && (
						<ErrorMessage>{errors.companyName.message}</ErrorMessage>
					)}
				</Field>
				<div className="grid grid-cols-2 gap-4">
					<Field>
						<Label>Company ID</Label>
						<Select {...register("companyIdType")}>
							<option value="">Select type...</option>
							{COMPANY_ID_TYPES.map((type) => (
								<option key={type.value} value={type.value}>
									{type.label}
								</option>
							))}
						</Select>
						{errors.companyIdType?.message && (
							<ErrorMessage>{errors.companyIdType.message}</ErrorMessage>
						)}
					</Field>
					<Field>
						<Label>Number</Label>
						<Input
							type="text"
							{...register("companyIdValue")}
							placeholder="Identifier"
						/>
						{errors.companyIdValue?.message && (
							<ErrorMessage>{errors.companyIdValue.message}</ErrorMessage>
						)}
					</Field>
				</div>
			</FieldGroup>

			<Button
				type="submit"
				color="dark/white"
				className="w-full"
				disabled={isSubmitting}
			>
				Sign up
			</Button>

			<div className="flex items-center justify-end">
				<div className="text-sm">
					<TextLink href="/auth/signin">
						Already have an account? Sign in
					</TextLink>
				</div>
			</div>
		</form>
	);
}
